export const VEHICLE_LINKED_EXPENSE_ENTRY_TYPES = ['fuel', 'toll', 'maintenance', 'breakdown'] as const;

export interface ExpenseLinkedWorkItemRef {
  id?: string | null;
  number?: string | null;
  type?: 'appointment' | 'job' | null;
}

export interface ExpenseLinkedInventoryRef {
  id?: string | null;
  sku?: string | null;
  name?: string | null;
  quantity?: number | null;
}

export interface ExpenseLinkageSummary {
  kind: 'vehicle' | 'appointment' | 'job' | 'work-item' | 'inventory';
  label: string;
  muted: boolean;
}

export function isVehicleLinkedExpenseType(type: string | null | undefined): boolean {
  const normalizedType = String(type || '').trim().toLowerCase();
  return normalizedType === 'vehicle_expense'
    || VEHICLE_LINKED_EXPENSE_ENTRY_TYPES.includes(normalizedType as typeof VEHICLE_LINKED_EXPENSE_ENTRY_TYPES[number]);
}

export function getExpenseLinkageSummary({
  vehicleRegistration,
  linkedWorkItem,
  linkedInventory,
  isVehicleLinked,
}: {
  vehicleRegistration?: string | null;
  linkedWorkItem?: ExpenseLinkedWorkItemRef | null;
  linkedInventory?: ExpenseLinkedInventoryRef | null;
  isVehicleLinked: boolean;
}): ExpenseLinkageSummary {
  const normalizedVehicleRegistration = typeof vehicleRegistration === 'string'
    ? vehicleRegistration.trim()
    : '';

  // Priority 1: Inventory linkage (highest priority for stock tracking)
  if (linkedInventory?.sku || linkedInventory?.id) {
    const label = linkedInventory.name || linkedInventory.sku || linkedInventory.id || 'Inventory item';
    const quantitySuffix = linkedInventory.quantity && linkedInventory.quantity > 0
      ? ` (${linkedInventory.quantity})`
      : '';
    return {
      kind: 'inventory',
      label: `${label}${quantitySuffix}`,
      muted: false,
    };
  }

  // Priority 2: Vehicle linkage
  if (normalizedVehicleRegistration) {
    return {
      kind: 'vehicle',
      label: normalizedVehicleRegistration,
      muted: false,
    };
  }

  // Priority 3: Work item linkage
  const linkedIdentifier = typeof linkedWorkItem?.number === 'string' && linkedWorkItem.number.trim()
    ? linkedWorkItem.number.trim()
    : typeof linkedWorkItem?.id === 'string' && linkedWorkItem.id.trim()
      ? linkedWorkItem.id.trim()
      : '';

  if (!isVehicleLinked) {
    if (linkedIdentifier) {
      const linkedType = linkedWorkItem?.type === 'appointment' ? 'appointment' : 'job';
      return {
        kind: linkedType,
        label: `${linkedType === 'appointment' ? 'Appointment' : 'Job'} ${linkedIdentifier}`,
        muted: false,
      };
    }

    return {
      kind: 'work-item',
      label: 'Job-linked purchase',
      muted: true,
    };
  }

  return {
    kind: 'vehicle',
    label: 'No vehicle',
    muted: true,
  };
}
